/**
 * ZUNO — ExtratoMotorista.js
 * ─────────────────────────────────────────────────────────────
 * Tela de extrato financeiro do motorista Flex.
 * Mostra:
 *  - Resumo do mês (corridas, ganhos brutos, taxa paga, líquido)
 *  - Histórico de cobranças diárias com status
 *  - Detalhes de cada corrida do dia
 *
 * ROTA: /extrato-motorista
 *
 * ADICIONAR no App.js:
 *   import ExtratoMotorista from "./pages/ExtratoMotorista";
 *   <Route path="/extrato-motorista" element={<ExtratoMotorista />} />
 *
 * CHAMAR do DriverHome (aba Config ou perfil):
 *   <button onClick={() => navigate("/extrato-motorista")}>
 *     Ver extrato financeiro
 *   </button>
 * ─────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection, query, where, orderBy, onSnapshot, getDoc, doc,
} from "firebase/firestore";

export default function ExtratoMotorista() {
  const navigate  = useNavigate();
  const [user, setUser]           = useState(null);
  const [plano, setPlano]         = useState(null);
  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [mesAtual, setMesAtual]   = useState(() => new Date().toISOString().slice(0, 7));
  const [expandido, setExpandido] = useState(null); // docId expandido

  // ── Auth ───────────────────────────────────────────────────
  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      try {
        const planoSnap = await getDoc(doc(db, "driver_planos", u.uid));
        if (planoSnap.exists()) setPlano(planoSnap.data());
      } catch (e) {}
    });
  }, [navigate]);

  // ── Cobranças do mês ───────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "driver_cobrancas"),
      where("driverUid", "==", user.uid),
      where("data", ">=", `${mesAtual}-01`),
      where("data", "<=", `${mesAtual}-31`),
      orderBy("data", "desc")
    );
    return onSnapshot(q, (snap) => {
      setCobrancas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [user, mesAtual]);

  // ── Resumo calculado ───────────────────────────────────────
  const resumo = {
    totalCorridas:  cobrancas.reduce((s, c) => s + (c.totalCorridas || 0), 0),
    totalBruto:     +cobrancas.reduce((s, c) => s + (c.totalBruto || 0), 0).toFixed(2),
    totalTaxa:      +cobrancas.reduce((s, c) => s + (c.totalTaxa || 0), 0).toFixed(2),
    totalPago:      +cobrancas.filter(c => c.status === "pago").reduce((s, c) => s + (c.totalTaxa || 0), 0).toFixed(2),
    totalPendente:  +cobrancas.filter(c => c.status !== "pago").reduce((s, c) => s + (c.totalTaxa || 0), 0).toFixed(2),
    diasTrabalhados: cobrancas.length,
    diasPagos:      cobrancas.filter(c => c.status === "pago").length,
    diasPendentes:  cobrancas.filter(c => c.status === "cobrado" || c.status === "inadimplente").length,
  };
  resumo.totalLiquido = +(resumo.totalBruto - resumo.totalTaxa).toFixed(2);

  // ── Helpers ────────────────────────────────────────────────
  const formatData = (d) => {
    if (!d) return "—";
    const [, m, dia] = d.split("-");
    const nomes = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
    return `${dia} ${nomes[parseInt(m) - 1]}`;
  };

  const formatHora = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const mesesDisponiveis = () => {
    const hoje = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      return d.toISOString().slice(0, 7);
    });
  };

  const nomeMes = (m) => {
    const [ano, mes] = m.split("-");
    const nomes = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
    return `${nomes[parseInt(mes) - 1]} ${ano}`;
  };

  const statusCfg = {
    pago:         { label: "Pago",             cor: "#22c55e", bg: "rgba(34,197,94,0.12)",    icone: "✅" },
    cobrado:      { label: "Aguard. pagamento", cor: "#f59e0b", bg: "rgba(245,158,11,0.12)",  icone: "⏳" },
    inadimplente: { label: "Inadimplente",      cor: "#ef4444", bg: "rgba(239,68,68,0.12)",   icone: "❌" },
    pendente:     { label: "Em aberto",         cor: "#6b7280", bg: "rgba(107,114,128,0.1)",  icone: "📋" },
  };

  const emTrial = plano?.trialAtivo && plano?.trialVencimento && new Date() < new Date(plano.trialVencimento);

  // ── RENDER ─────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#0a0e1a", color: "#fff", fontFamily: "system-ui, sans-serif", paddingBottom: 40 }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        * { box-sizing: border-box; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "52px 16px 16px", background: "#0d1120", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <button
          onClick={() => navigate(-1)}
          style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          ←
        </button>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Extrato financeiro</h1>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", margin: 0 }}>
            {plano?.plano === "flex" ? "Plano Flex — 8% por corrida" : "Plano Mensalidade"}
          </p>
        </div>
      </div>

      {/* Trial banner */}
      {emTrial && (
        <div style={{ margin: "16px 16px 0", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🎉</span>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#22c55e", margin: 0 }}>Período grátis ativo!</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: 0 }}>
              Nenhuma taxa é cobrada durante o trial. O extrato abaixo mostra apenas para referência.
            </p>
          </div>
        </div>
      )}

      {/* Seletor de mês */}
      <div style={{ padding: "16px 16px 0" }}>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
          {mesesDisponiveis().map(m => (
            <button
              key={m}
              onClick={() => setMesAtual(m)}
              style={{
                padding: "6px 14px", borderRadius: 20, border: "1px solid",
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                whiteSpace: "nowrap", flexShrink: 0,
                background: mesAtual === m ? "rgba(168,85,247,0.2)" : "transparent",
                color: mesAtual === m ? "#a855f7" : "rgba(255,255,255,0.4)",
                borderColor: mesAtual === m ? "rgba(168,85,247,0.4)" : "rgba(255,255,255,0.1)",
              }}
            >
              {nomeMes(m)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.1)", borderTopColor: "#a855f7", animation: "spin 0.8s linear infinite" }} />
        </div>
      ) : (
        <>
          {/* Cards de resumo */}
          <div style={{ padding: "16px 16px 0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>

              {/* Ganho bruto */}
              <div style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "14px 16px" }}>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Ganho bruto</p>
                <p style={{ fontSize: 22, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>R$ {resumo.totalBruto.toFixed(2)}</p>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: 0 }}>{resumo.totalCorridas} corrida{resumo.totalCorridas !== 1 ? "s" : ""}</p>
              </div>

              {/* Líquido (depois da taxa) */}
              <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 14, padding: "14px 16px" }}>
                <p style={{ fontSize: 11, color: "rgba(34,197,94,0.7)", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Você recebeu</p>
                <p style={{ fontSize: 22, fontWeight: 700, color: "#22c55e", margin: "0 0 2px" }}>R$ {resumo.totalLiquido.toFixed(2)}</p>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: 0 }}>após taxa Zuno</p>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
              <div style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "12px" }}>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>Taxa total</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: "#a855f7", margin: 0 }}>R$ {resumo.totalTaxa.toFixed(2)}</p>
              </div>
              <div style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "12px" }}>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>Pago</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: "#22c55e", margin: 0 }}>R$ {resumo.totalPago.toFixed(2)}</p>
              </div>
              <div style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "12px" }}>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>Pendente</p>
                <p style={{ fontSize: 16, fontWeight: 700, color: resumo.totalPendente > 0 ? "#f59e0b" : "#22c55e", margin: 0 }}>
                  R$ {resumo.totalPendente.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Barra de progresso pago vs total */}
            {resumo.totalTaxa > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Taxa paga</span>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{Math.round((resumo.totalPago / resumo.totalTaxa) * 100)}%</span>
                </div>
                <div style={{ height: 6, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 3,
                    width: `${Math.min(100, (resumo.totalPago / resumo.totalTaxa) * 100)}%`,
                    background: "linear-gradient(90deg, #22c55e, #16a34a)",
                    transition: "width 0.6s ease",
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Lista de dias */}
          <div style={{ padding: "0 16px" }}>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px", fontWeight: 600 }}>
              Histórico de dias trabalhados
            </p>

            {cobrancas.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.2)" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                <p style={{ fontSize: 14 }}>Nenhuma corrida em {nomeMes(mesAtual)}</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {cobrancas.map((cob, idx) => {
                  const cfg = statusCfg[cob.status] || statusCfg.pendente;
                  const aberto = expandido === cob.id;
                  const liquido = +((cob.totalBruto || 0) - (cob.totalTaxa || 0)).toFixed(2);

                  return (
                    <div
                      key={cob.id}
                      style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden", animation: `fadeUp 0.3s ease ${idx * 0.04}s both` }}
                    >
                      {/* Linha principal — clicável para expandir */}
                      <div
                        onClick={() => setExpandido(aberto ? null : cob.id)}
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer" }}
                      >
                        {/* Data */}
                        <div style={{ width: 44, textAlign: "center", flexShrink: 0 }}>
                          <p style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>{formatData(cob.data).split(" ")[0]}</p>
                          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: 0, textTransform: "uppercase" }}>{formatData(cob.data).split(" ")[1]}</p>
                        </div>

                        {/* Corridas + líquido */}
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: "#fff", margin: "0 0 2px" }}>
                            {cob.totalCorridas} corrida{cob.totalCorridas !== 1 ? "s" : ""}
                          </p>
                          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0 }}>
                            Bruto R$ {(cob.totalBruto || 0).toFixed(2)} · Líquido R$ {liquido.toFixed(2)}
                          </p>
                        </div>

                        {/* Taxa + status */}
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <p style={{ fontSize: 14, fontWeight: 700, color: "#a855f7", margin: "0 0 3px" }}>
                            −R$ {(cob.totalTaxa || 0).toFixed(2)}
                          </p>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, background: cfg.bg }}>
                            <span style={{ fontSize: 10 }}>{cfg.icone}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: cfg.cor }}>{cfg.label}</span>
                          </div>
                        </div>

                        {/* Chevron */}
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", transition: "transform 0.2s", transform: aberto ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                      </div>

                      {/* Detalhes expandidos */}
                      {aberto && (
                        <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "10px 14px 14px" }}>

                          {/* Corridas do dia */}
                          {cob.corridas?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 8px", fontWeight: 600 }}>
                                Corridas do dia
                              </p>
                              {cob.corridas.map((c, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8, marginBottom: 4 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#a855f7", flexShrink: 0 }} />
                                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                                      {c.hora ? formatHora(c.hora) : `Corrida ${i + 1}`}
                                    </span>
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <span style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}>R$ {(c.valorCorrida || 0).toFixed(2)}</span>
                                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginLeft: 6 }}>taxa −R$ {(c.taxa || 0).toFixed(2)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Info de pagamento */}
                          <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: "10px 12px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Total bruto</span>
                              <span style={{ fontSize: 12, color: "#fff" }}>R$ {(cob.totalBruto || 0).toFixed(2)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Taxa Zuno (8%)</span>
                              <span style={{ fontSize: 12, color: "#a855f7" }}>−R$ {(cob.totalTaxa || 0).toFixed(2)}</span>
                            </div>
                            <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "6px 0" }} />
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Você recebeu</span>
                              <span style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>R$ {liquido.toFixed(2)}</span>
                            </div>
                          </div>

                          {/* Status do pagamento */}
                          {cob.status === "pago" && (
                            <p style={{ fontSize: 11, color: "rgba(34,197,94,0.7)", marginTop: 8, textAlign: "center" }}>
                              ✅ Taxa paga{cob.confirmadoPor ? ` · confirmado por ${cob.confirmadoPor}` : ""}
                            </p>
                          )}
                          {(cob.status === "cobrado" || cob.status === "inadimplente") && (
                            <div style={{ marginTop: 10, padding: "10px 12px", background: cob.status === "inadimplente" ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)", borderRadius: 10, border: `1px solid ${cob.status === "inadimplente" ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)"}` }}>
                              <p style={{ fontSize: 12, color: cob.status === "inadimplente" ? "#ef4444" : "#f59e0b", margin: "0 0 4px", fontWeight: 600 }}>
                                {cob.status === "inadimplente" ? "❌ Pagamento em atraso" : "⏳ Aguardando pagamento"}
                              </p>
                              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: 0 }}>
                                {cob.pixVencimento
                                  ? `Prazo: ${new Date(cob.pixVencimento).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                                  : "Acesse o app para pagar via Pix"}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Rodapé informativo */}
          {cobrancas.length > 0 && (
            <div style={{ margin: "20px 16px 0", padding: "14px 16px", background: "rgba(168,85,247,0.05)", border: "1px solid rgba(168,85,247,0.12)", borderRadius: 14 }}>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", margin: 0, lineHeight: 1.6 }}>
                ⚡ <strong style={{ color: "rgba(255,255,255,0.6)" }}>Plano Flex:</strong> O valor da corrida é 100% do motorista. A obrigação de 8% com a Zuno é registrada separadamente e cobrada no ciclo do plano.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
